/**
 * 模块: useSearchPerformance
 * 职责: 搜索查询绩效数据管理Hook - 主入口
 * 限制: <280行
 */

import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import type { UseSearchPerformanceResult } from './types';
import type {
  SearchPerformancePeriod,
  AdvantageKeywordsResult,
  MarketOverview,
  OpportunityKeywordsResult,
  FunnelAnalysis,
  DeliveryAnalysis,
  MarketTrends,
} from '../../types';
import type { AnalysisParams } from '../../services/searchPerformanceAPI';
import {
  createFetchPeriodsHandler,
  createFetchAdvantageKeywordsHandler,
  createFetchMarketOverviewHandler,
  createFetchOpportunityKeywordsHandler,
  createFetchFunnelAnalysisHandler,
  createFetchDeliveryAnalysisHandler,
  createFetchMarketTrendsHandler,
  createFetchKeywordPivotHandler,
} from './fetchHandlers';
import {
  createUploadCSVHandler,
  createDeletePeriodHandler,
} from './operationHandlers';

export type { UseSearchPerformanceResult } from './types';

export const useSearchPerformance = (skuId: number): UseSearchPerformanceResult => {
  const { message } = App.useApp();

  // 状态管理
  const [periods, setPeriods] = useState<SearchPerformancePeriod[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);

  const [advantageData, setAdvantageData] = useState<AdvantageKeywordsResult | null>(null);
  const [marketOverview, setMarketOverview] = useState<MarketOverview | null>(null);
  const [opportunityData, setOpportunityData] = useState<OpportunityKeywordsResult | null>(null);
  const [funnelData, setFunnelData] = useState<FunnelAnalysis | null>(null);
  const [deliveryData, setDeliveryData] = useState<DeliveryAnalysis | null>(null);
  const [trendsData, setTrendsData] = useState<MarketTrends | null>(null);
  const [keywordPivotData, setKeywordPivotData] = useState<any | null>(null);

  const [loading, setLoading] = useState(false);

  // 创建fetch处理器（使用useCallback保持引用稳定）
  const fetchPeriods = useCallback(
    () => createFetchPeriodsHandler(skuId, setPeriods, setPeriodsLoading, message)(),
    [skuId, message]
  );

  const fetchAdvantageKeywords = useCallback(
    async (params?: AnalysisParams) => createFetchAdvantageKeywordsHandler(skuId, setAdvantageData, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchMarketOverview = useCallback(
    async (params?: AnalysisParams) => createFetchMarketOverviewHandler(skuId, setMarketOverview, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchOpportunityKeywords = useCallback(
    async (params?: AnalysisParams) => createFetchOpportunityKeywordsHandler(skuId, setOpportunityData, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchFunnelAnalysis = useCallback(
    async (params?: AnalysisParams) => createFetchFunnelAnalysisHandler(skuId, setFunnelData, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchDeliveryAnalysis = useCallback(
    async (params?: AnalysisParams) => createFetchDeliveryAnalysisHandler(skuId, setDeliveryData, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchMarketTrends = useCallback(
    async (params?: AnalysisParams) => createFetchMarketTrendsHandler(skuId, setTrendsData, setLoading, message)(skuId, params),
    [skuId, message]
  );

  const fetchKeywordPivot = useCallback(
    () => createFetchKeywordPivotHandler(skuId, setKeywordPivotData, setLoading, message)(),
    [skuId, message]
  );

  // 创建操作处理器
  const uploadCSV = useCallback(
    (file: File) => createUploadCSVHandler(skuId, setLoading, fetchPeriods, message)(file),
    [skuId, fetchPeriods, message]
  );

  const deletePeriod = useCallback(
    (periodId: number) => createDeletePeriodHandler(setLoading, fetchPeriods, message)(periodId),
    [fetchPeriods, message]
  );

  // 初始化时加载周期列表
  useEffect(() => {
    console.log('[useSearchPerformance] 🚀 初始化，准备加载周期列表');
    fetchPeriods();
  }, [fetchPeriods]);

  // 监控 periods 和 keywordPivotData 的变化
  useEffect(() => {
    console.log('[useSearchPerformance] 📊 状态变化:', {
      periodsCount: periods.length,
      periods: periods,
      keywordPivotData: keywordPivotData,
      hasKeywordPivotData: !!keywordPivotData
    });
  }, [periods, keywordPivotData]);

  return {
    periods,
    periodsLoading,
    advantageData,
    marketOverview,
    opportunityData,
    funnelData,
    deliveryData,
    trendsData,
    keywordPivotData,
    loading,
    fetchPeriods,
    fetchAdvantageKeywords,
    fetchMarketOverview,
    fetchOpportunityKeywords,
    fetchFunnelAnalysis,
    fetchDeliveryAnalysis,
    fetchMarketTrends,
    fetchKeywordPivot,
    uploadCSV,
    deletePeriod,
  };
};

export default useSearchPerformance;

